import React, { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useData } from "../context/DataContext";
import {
  Box,
  Typography,
  Slider,
  Autocomplete,
  TextField,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Skeleton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from "recharts";

/* =========================
   Types
========================= */
type MatchRow = {
  dateRaw: string;
  year: number;
  homeTeam: string;
  awayTeam: string;
  homeGoals: number;
  awayGoals: number;
};

interface YearlyData {
  year: number;
  averageGoals: number;
}

interface WinData {
  team: string;
  total_wins: number;
  home_wins: number;
  away_wins: number;
  home_win_pct: number; // 0..1
  away_win_pct: number; // 0..1
}

type WinLossYearPoint = { year: number; wins: number; losses: number };

/* =========================
   Theme
========================= */
const palette = {
  bg: "#EEF5FF",
  panel: "#B4D4FF",
  card: "#FFFFFF",
  border: "#86B6F6",
  ink: "#0B2230",
  accent: "#176B87",
  soft: "rgba(23,107,135,0.08)",
};

const shellSx = {
  mx: "auto",
  width: "100%",
  maxWidth: 1200,
  px: { xs: 2, sm: 3 },
};

const cardSx = {
  backgroundColor: palette.card,
  border: `1px solid ${palette.border}`,
  borderRadius: 3,
  boxShadow: "0 10px 24px rgba(23,107,135,0.10)",
  p: { xs: 2, sm: 3 },
  position: "relative" as const,
  overflow: "hidden",
};

const sectionTitleSx = {
  fontWeight: 1000,
  color: palette.ink,
  fontSize: { xs: 16, sm: 18, md: 20 },
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const chartHeightSx = { xs: 280, sm: 340, md: 380 };

const tooltipStyle = {
  borderRadius: 12,
  border: `1px solid ${palette.border}`,
  background: "rgba(255,255,255,0.96)",
} as const;

/* =========================
   Parsing (datatype-proof)
========================= */
function safeYearFromDate(dateStr: string): number | null {
  const s = (dateStr || "").trim();
  if (!s) return null;

  const m = s.match(/(\d{4})/);
  if (m) {
    const y = Number(m[1]);
    if (Number.isFinite(y) && y >= 1800 && y <= 2200) return y;
  }

  const d = new Date(s);
  const y2 = d.getFullYear();
  if (Number.isFinite(y2) && y2 >= 1800 && y2 <= 2200) return y2;

  return null;
}

/* =========================
   Compute charts from matches
========================= */
function computeFromMatches(matches: MatchRow[]) {
  const goalByYear: Record<number, { totalGoals: number; matches: number }> = {};
  const winStats: Record<string, { total: number; home: number; away: number }> = {};

  for (const m of matches) {
    const year = m.year;

    if (!goalByYear[year]) goalByYear[year] = { totalGoals: 0, matches: 0 };
    goalByYear[year].totalGoals += m.homeGoals + m.awayGoals;
    goalByYear[year].matches += 1;

    let winner = "";
    if (m.homeGoals > m.awayGoals) winner = m.homeTeam;
    else if (m.awayGoals > m.homeGoals) winner = m.awayTeam;

    if (winner) {
      if (!winStats[winner]) winStats[winner] = { total: 0, home: 0, away: 0 };
      winStats[winner].total++;
      if (winner === m.homeTeam) winStats[winner].home++;
      else winStats[winner].away++;
    }
  }

  const avgGoalsData: YearlyData[] = Object.entries(goalByYear)
    .map(([y, v]) => ({
      year: Number(y),
      averageGoals: +(v.matches ? v.totalGoals / v.matches : 0).toFixed(2),
    }))
    .sort((a, b) => a.year - b.year);

  const winData: WinData[] = Object.entries(winStats)
    .map(([team, s]) => ({
      team,
      total_wins: s.total,
      home_wins: s.home,
      away_wins: s.away,
      home_win_pct: +(s.total ? s.home / s.total : 0).toFixed(2),
      away_win_pct: +(s.total ? s.away / s.total : 0).toFixed(2),
    }))
    .sort((a, b) => b.total_wins - a.total_wins);

  const allYears = matches.map((m) => m.year);
  const uniqueTeams = Array.from(new Set(matches.flatMap((m) => [m.homeTeam, m.awayTeam]))).sort();

  return { avgGoalsData, winData, allYears, uniqueTeams };
}

/* =========================
   Trend computed for a specific team
========================= */
function computeWinLossSeriesForTeam(matches: MatchRow[], team: string): WinLossYearPoint[] {
  const byYear: Record<number, WinLossYearPoint> = {};

  for (const m of matches) {
    if (m.homeTeam !== team && m.awayTeam !== team) continue;

    if (!byYear[m.year]) byYear[m.year] = { year: m.year, wins: 0, losses: 0 };

    const isDraw = m.homeGoals === m.awayGoals;
    if (isDraw) continue;

    const winner = m.homeGoals > m.awayGoals ? m.homeTeam : m.awayTeam;
    const loser = winner === m.homeTeam ? m.awayTeam : m.homeTeam;

    if (winner === team) byYear[m.year].wins += 1;
    if (loser === team) byYear[m.year].losses += 1;
  }

  return Object.values(byYear).sort((a, b) => a.year - b.year);
}

/* =========================
   Hook: load CSV -> matches (via shared DataContext)
========================= */
function useCsvData() {
  const { rows, loading, error } = useData();

  const matches = useMemo<MatchRow[]>(() => {
    return rows
      .map((r) => {
        const year = safeYearFromDate(r.date);
        const homeGoals = Number.parseInt(r.home_score, 10);
        const awayGoals = Number.parseInt(r.away_score, 10);
        if (year === null) return null;
        if (!Number.isFinite(homeGoals) || Number.isNaN(homeGoals)) return null;
        if (!Number.isFinite(awayGoals) || Number.isNaN(awayGoals)) return null;
        if (!r.home_team || !r.away_team) return null;
        return {
          dateRaw: r.date,
          year,
          homeTeam: r.home_team,
          awayTeam: r.away_team,
          homeGoals,
          awayGoals,
        } as MatchRow;
      })
      .filter((m): m is MatchRow => m !== null);
  }, [rows]);

  return { matches, loading, errorMsg: error };
}

/* =========================
   UI helpers
========================= */
function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <Box
      sx={{
        border: `1px dashed rgba(134,182,246,0.9)`,
        borderRadius: 3,
        p: 2.5,
        background: palette.soft,
        color: palette.ink,
      }}
    >
      <Typography sx={{ fontWeight: 1000 }}>{title}</Typography>
      <Typography sx={{ mt: 0.6, color: "rgba(11,34,48,0.72)", lineHeight: 1.7 }}>
        {body}
      </Typography>
    </Box>
  );
}

function ChartHeader({
  title,
  helpTitle,
  helpBody,
  rightSlot,
}: {
  title: string;
  helpTitle: string;
  helpBody: React.ReactNode;
  rightSlot?: React.ReactNode;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2, mb: 1.4 }}>
      <Typography sx={sectionTitleSx}>
        {title}
        <Tooltip
          placement="top"
          arrow
          enterTouchDelay={0}
          title={
            <Box sx={{ maxWidth: 320, p: 0.2 }}>
              <Typography sx={{ fontWeight: 1000, mb: 0.5 }}>{helpTitle}</Typography>
              <Box sx={{ color: "rgba(11,34,48,0.80)", lineHeight: 1.55 }}>{helpBody}</Box>
            </Box>
          }
        >
          <IconButton
            size="small"
            sx={{
              ml: 0.2,
              color: palette.accent,
              border: `1px solid rgba(134,182,246,0.7)`,
              backgroundColor: "rgba(255,255,255,0.7)",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.95)" },
            }}
            aria-label={`${title} info`}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Typography>

      <Box sx={{ flex: 1 }} />
      {rightSlot}
    </Box>
  );
}

/* =========================
   Filters (Sticky)
========================= */
type FiltersBarProps = {
  minYear: number;
  maxYear: number;
  yearRange: [number, number];
  setYearRange: (r: [number, number]) => void;
  uniqueTeams: string[];
  selectedTeams: string[];
  setSelectedTeams: React.Dispatch<React.SetStateAction<string[]>>;
};

const FiltersBar: React.FC<FiltersBarProps> = ({
  minYear,
  maxYear,
  yearRange,
  setYearRange,
  uniqueTeams,
  selectedTeams,
  setSelectedTeams,
}) => {
  const handleYearRangeChange = (_: Event, val: number | number[]) => {
    if (Array.isArray(val)) setYearRange([Number(val[0]), Number(val[1])]);
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        left: 0,
        right: 0,
        width: "100%",
        zIndex: 1300,
        backdropFilter: "saturate(180%) blur(10px)",
        backgroundColor: "rgba(238,245,255,0.92)",
        borderBottom: `1px solid rgba(134,182,246,0.55)`,
        pt: 2,
        pb: 2,
      }}
    >
      <Box sx={{ ...shellSx }}>
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography sx={{ fontWeight: 900, color: palette.ink }}>Filter by Year Range</Typography>
              <Chip
                size="small"
                label={`${yearRange[0]} – ${yearRange[1]}`}
                sx={{
                  bgcolor: "#FFFFFF",
                  border: `1px solid ${palette.border}`,
                  fontWeight: 900,
                  color: palette.ink,
                }}
              />
            </Box>
            <Slider
              value={yearRange}
              min={minYear}
              max={maxYear}
              step={1}
              disableSwap
              onChange={handleYearRangeChange}
              valueLabelDisplay="off"
              sx={{ color: palette.accent, "& .MuiSlider-rail": { opacity: 0.35 } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography sx={{ fontWeight: 900, color: palette.ink }}>Trend Team</Typography>
              <Chip
                size="small"
                label="affects Win/Loss chart only"
                sx={{
                  bgcolor: palette.soft,
                  border: `1px solid ${palette.border}`,
                  color: "rgba(11,34,48,0.65)",
                  fontWeight: 700,
                  fontSize: 11,
                }}
              />
            </Box>
            <Autocomplete
              options={uniqueTeams}
              value={selectedTeams[0] ?? null}
              onChange={(_, value) => setSelectedTeams(value ? [value] : [])}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select a team"
                  placeholder="e.g. Argentina"
                  variant="outlined"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#FFFFFF",
                      borderRadius: 2,
                    },
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

/* =========================
   Charts
========================= */
const AvgGoalsChart: React.FC<{ data: YearlyData[] }> = ({ data }) => {
  return (
    <Box sx={{ ...cardSx, height: "100%" }}>
      <ChartHeader
        title="Average Goals per Match by Year"
        helpTitle="What this shows"
        helpBody={
          <>
            <Typography variant="body2">
              Tracks the <b>average total points per match</b> (home + away) for each year.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Use it to spot <b>era shifts</b> or unusually high/low scoring periods.
            </Typography>
          </>
        }
        rightSlot={
          <Chip
            size="small"
            label={`${data.length} years`}
            sx={{
              bgcolor: palette.soft,
              border: `1px solid ${palette.border}`,
              fontWeight: 900,
              color: palette.ink,
            }}
          />
        }
      />

      {data.length === 0 ? (
        <EmptyState title="No data in this filter range" body="Widen the year range to include match years." />
      ) : (
        <Box sx={{ width: "100%", height: chartHeightSx }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid stroke={palette.border} strokeOpacity={0.35} strokeDasharray="3 3" />
              <XAxis dataKey="year" tick={{ fill: palette.ink }} />
              <YAxis tick={{ fill: palette.ink }} />
              <ReTooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="averageGoals" stroke={palette.accent} strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

const TopWinsChart: React.FC<{ data: WinData[] }> = ({ data }) => {
  const top10 = data.slice(0, 10);

  return (
    <Box sx={{ ...cardSx, height: "100%" }}>
      <ChartHeader
        title="Top Teams by Total Wins"
        helpTitle="What this shows"
        helpBody={
          <Typography variant="body2">
            Ranks all teams by <b>total wins</b> within the selected year range. This chart is <b>not</b> affected by the team selector — it always shows the full ranking.
          </Typography>
        }
        rightSlot={
          <Chip
            size="small"
            label={`Top ${Math.min(10, data.length)}`}
            sx={{
              bgcolor: palette.soft,
              border: `1px solid ${palette.border}`,
              fontWeight: 900,
              color: palette.ink,
            }}
          />
        }
      />

      {top10.length === 0 ? (
        <EmptyState title="No teams to rank" body="Widen the year range or clear the team filter." />
      ) : (
        <Box sx={{ width: "100%", height: chartHeightSx }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={top10} layout="vertical" margin={{ top: 10, right: 20, left: 70, bottom: 10 }}>
              <CartesianGrid stroke={palette.border} strokeOpacity={0.35} strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fill: palette.ink }} />
              <YAxis type="category" dataKey="team" width={140} tick={{ fill: palette.ink }} />
              <ReTooltip contentStyle={tooltipStyle} />
              <Bar dataKey="total_wins" fill={palette.accent} radius={[10, 10, 10, 10]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

const HomeAwayPctChart: React.FC<{ data: WinData[] }> = ({ data }) => {
  const top10 = data.slice(0, 10);

  // Flatten into rows usable by a grouped horizontal bar
  const chartData = top10.map((d) => ({
    team: d.team,
    "Home Win %": Math.round(d.home_win_pct * 100),
    "Away Win %": Math.round(d.away_win_pct * 100),
  }));

  return (
    <Box sx={{ ...cardSx, height: "100%" }}>
      <ChartHeader
        title="Home vs Away Win %"
        helpTitle="What this shows"
        helpBody={
          <Typography variant="body2">
            For the top 10 teams by total wins, shows what share of their wins came at <b>home</b> vs <b>away</b>. Affected only by the year range — not the team selector.
          </Typography>
        }
      />

      {chartData.length === 0 ? (
        <EmptyState title="No data in range" body="Widen the year range to see results." />
      ) : (
        <Box sx={{ width: "100%", height: chartHeightSx }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 30, left: 80, bottom: 10 }}>
              <CartesianGrid stroke={palette.border} strokeOpacity={0.35} strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fill: palette.ink }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
              <YAxis type="category" dataKey="team" width={76} tick={{ fill: palette.ink, fontSize: 12 }} />
              <ReTooltip contentStyle={tooltipStyle} formatter={(v: any) => `${v}%`} />
              <Legend verticalAlign="top" align="right" />
              <Bar dataKey="Home Win %" fill={palette.accent} radius={[0, 6, 6, 0]} />
              <Bar dataKey="Away Win %" fill={palette.border} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

/* =========================
   UPDATED Trend Chart:
   - removed team picker
   - if selectedTeams is empty => show blank (no data)
   - otherwise uses the "top" team from winData (already filtered by selection + years)
========================= */
const WinLossTrendChart: React.FC<{
  series: WinLossYearPoint[];
  focusTeam: string;
  isBlank: boolean;
}> = ({ series, focusTeam, isBlank }) => {
  return (
    <Box sx={{ ...cardSx, height: "100%" }}>
      <ChartHeader
        title="Win and Loss Trends by Year"
        helpTitle="What this shows"
        helpBody={
          <Typography variant="body2">
            Shows the selected team's win and loss count per year. Pick a team in the "Trend Team" filter to populate this chart. The other charts (Top Wins, Avg Goals, Home/Away %) are <b>not</b> affected by this selection.
          </Typography>
        }
        rightSlot={
          <Chip
            size="small"
            label={isBlank ? "No team selected" : `Team: ${focusTeam || "—"}`}
            sx={{
              bgcolor: "#FFFFFF",
              border: `1px solid ${palette.border}`,
              fontWeight: 900,
              color: palette.ink,
            }}
          />
        }
      />

      {isBlank ? (
        <EmptyState
          title="No team selected"
          body="Select at least one team in the filter to generate the win/loss trend."
        />
      ) : series.length === 0 ? (
        <EmptyState
          title="No trend data available"
          body="This team has no non-draw matches in the current year range."
        />
      ) : (
        <Box sx={{ width: "100%", height: chartHeightSx }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <CartesianGrid stroke={palette.border} strokeOpacity={0.35} strokeDasharray="3 3" />
              <XAxis dataKey="year" tick={{ fill: palette.ink }} />
              <YAxis tick={{ fill: palette.ink }} allowDecimals={false} />
              <ReTooltip contentStyle={tooltipStyle} />
              <Legend verticalAlign="bottom" align="center" />
              <Line type="monotone" dataKey="wins" name="Wins" stroke={palette.accent} strokeWidth={3} dot={false} />
              <Line type="monotone" dataKey="losses" name="Losses" stroke={palette.border} strokeWidth={3} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};

/* =========================
   Main Component
========================= */
const MatchStats: React.FC = () => {
  const { matches, loading, errorMsg } = useCsvData();
  const [searchParams, setSearchParams] = useSearchParams();

  const allYears = useMemo(() => matches.map((m) => m.year), [matches]);
  const minYear = allYears.length ? Math.min(...allYears) : 1871;
  const maxYear = allYears.length ? Math.max(...allYears) : 2022;

  const uniqueTeams = useMemo(() => {
    const s = new Set<string>();
    for (const m of matches) { s.add(m.homeTeam); s.add(m.awayTeam); }
    return Array.from(s).sort();
  }, [matches]);

  // --- URL-persisted filter state ---
  const [yearRange, setYearRange] = useState<[number, number]>([minYear, maxYear]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>(() => {
    const raw = searchParams.get("teams");
    return raw ? raw.split(",").filter(Boolean) : [];
  });

  // Once data loads, initialise year range from URL or dataset bounds
  const [rangeInitialised, setRangeInitialised] = useState(false);
  useEffect(() => {
    if (!matches.length || rangeInitialised) return;
    const parseYear = (key: string, fallback: number) => {
      const v = Number(searchParams.get(key));
      return Number.isFinite(v) && v >= 1800 && v <= 2200 ? v : fallback;
    };
    const y0 = parseYear("y0", minYear);
    const y1 = parseYear("y1", maxYear);
    setYearRange([
      Math.max(minYear, Math.min(y0, maxYear)),
      Math.max(minYear, Math.min(y1, maxYear)),
    ]);
    setRangeInitialised(true);
  }, [matches.length, minYear, maxYear, rangeInitialised]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep URL in sync with filter state
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("y0", String(yearRange[0]));
    next.set("y1", String(yearRange[1]));
    if (selectedTeams.length) next.set("teams", selectedTeams.join(","));
    else next.delete("teams");
    setSearchParams(next, { replace: true });
  }, [yearRange, selectedTeams]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── TWO filtered sets ───────────────────────────────────────────────────
  // yearOnlyMatches: year range ONLY → feeds Top Wins, Avg Goals, Home/Away %
  const yearOnlyMatches = useMemo(() => {
    const [y0, y1] = yearRange;
    return matches.filter((m) => m.year >= y0 && m.year <= y1);
  }, [matches, yearRange]);

  // Charts that should NOT be affected by team selection
  const { avgGoalsData, winData } = useMemo(
    () => computeFromMatches(yearOnlyMatches),
    [yearOnlyMatches]
  );

  // Win/loss trend: uses the first selected team (or clears when nothing selected)
  const focusTrendTeam = useMemo(
    () => (selectedTeams.length ? selectedTeams[0] : ""),
    [selectedTeams]
  );
  const trendIsBlank = !focusTrendTeam;

  const winLossSeries = useMemo(() => {
    if (trendIsBlank) return [];
    return computeWinLossSeriesForTeam(yearOnlyMatches, focusTrendTeam);
  }, [yearOnlyMatches, focusTrendTeam, trendIsBlank]);

  const matchCountLabel = `${yearOnlyMatches.length.toLocaleString()} matches`;

  return (
    <Box sx={{ backgroundColor: palette.bg, minHeight: "100vh", pb: 4 }}>
      {/* Header */}
      <Box sx={{ ...shellSx, pt: { xs: 3, sm: 4 }, pb: 2 }}>
        <Typography
          variant="h3"
          textAlign="center"
          sx={{
            fontFamily: "Poppins",
            fontWeight: 1000,
            color: palette.accent,
            fontSize: { xs: 24, sm: 34, md: 42 },
          }}
        >
          Match Statistics Dashboard
        </Typography>

        <Stack direction="row" spacing={1} justifyContent="center" useFlexGap flexWrap="wrap" sx={{ mt: 1.4 }}>
          <Chip
            label={loading ? "Loading…" : matchCountLabel}
            sx={{ bgcolor: "#FFFFFF", border: `1px solid ${palette.border}`, color: palette.ink, fontWeight: 900 }}
          />
          <Chip
            label={selectedTeams.length ? `Trend: ${focusTrendTeam}` : "Select a team for trend"}
            sx={{ bgcolor: "#FFFFFF", border: `1px solid ${palette.border}`, color: palette.ink, fontWeight: 900 }}
          />
        </Stack>

        {/* Error banner with retry */}
        {errorMsg && (
          <Box
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid rgba(138,31,31,0.35)",
              backgroundColor: "rgba(138,31,31,0.06)",
              p: 1.8,
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 1000, color: "#8A1F1F" }}>Could not load data</Typography>
              <Typography sx={{ mt: 0.5, color: "rgba(11,34,48,0.75)", fontSize: 13 }}>
                {errorMsg} — make sure <b>/public/data/results.csv</b> exists.
              </Typography>
            </Box>
            <Box
              component="button"
              onClick={() => window.location.reload()}
              sx={{
                border: "1px solid rgba(138,31,31,0.4)",
                borderRadius: 2,
                background: "rgba(138,31,31,0.08)",
                color: "#8A1F1F",
                fontWeight: 800,
                px: 2,
                py: 0.8,
                cursor: "pointer",
                fontSize: 13,
                "&:hover": { background: "rgba(138,31,31,0.14)" },
              }}
            >
              Retry
            </Box>
          </Box>
        )}
      </Box>

      {/* Filters */}
      <FiltersBar
        minYear={minYear}
        maxYear={maxYear}
        yearRange={yearRange}
        setYearRange={setYearRange}
        uniqueTeams={uniqueTeams}
        selectedTeams={selectedTeams}
        setSelectedTeams={setSelectedTeams}
      />

      <Divider sx={{ opacity: 0 }} />

      {/* Charts */}
      <Box sx={{ ...shellSx, mt: 3 }}>
        {loading ? (
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {[380, 300, 300, 300].map((h, i) => (
              <Grid item xs={12} md={i >= 2 ? 6 : 12} key={i}>
                <Box sx={{ ...cardSx }}>
                  <Skeleton variant="text" width="45%" height={28} sx={{ mb: 1.5 }} />
                  <Skeleton variant="rectangular" height={h} sx={{ borderRadius: 2 }} />
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {/* Top Wins: year filter only — NOT affected by team selection */}
            <Grid item xs={12}>
              <TopWinsChart data={winData} />
            </Grid>

            {/* Win/Loss trend: team-specific */}
            <Grid item xs={12}>
              <WinLossTrendChart series={winLossSeries} focusTeam={focusTrendTeam} isBlank={trendIsBlank} />
            </Grid>

            {/* Avg goals: year filter only */}
            <Grid item xs={12} md={6}>
              <AvgGoalsChart data={avgGoalsData} />
            </Grid>

            {/* Home/Away %: year filter only */}
            <Grid item xs={12} md={6}>
              <HomeAwayPctChart data={winData} />
            </Grid>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default MatchStats;
